<template>
  <div class="ds-width-full case-study">
    <!-- Header -->
    <DsSection
      :with-space-top="false"
      :class="sectionIntroduction"
    >
      <div ref="media" class="ds-full-screen-content ds-padding-x-24">
        <div class="header-media-container">
          <img
            v-if="caseStudy.headerMedia.isImage"
            :src="caseStudy.headerMedia.original.src"
            :alt="caseStudy.headerMedia.original.name"
            class="header-media"
          >
          <video
            v-else-if="caseStudy.headerMedia.isVideo"
            width="100%"
            height="100%"
            loop
            autoplay
            playsinline
            muted
            :poster="caseStudy.headerMedia?.thumbnail?.original?.src"
            class="header-media"
          >
            <source :src="caseStudy.headerMedia.src" type="video/mp4">
          </video>
        </div>
      </div>
      <div class="ds-grid ds-position-relative ds-md-margin-top-144 ds-sm-margin-top-48">
        <div
          ref="infoText"
          class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--7 info-text"
        >
          <div ref="infoEyebrow" class="info-eyebrow ds-text-medium ds-margin-bottom-12">
            {{ caseStudy.eyebrow }}
          </div>
          <h1 ref="infoTitle" class="infoTitle" :class="isScreenLarge ? 'ds-heading-large' : 'ds-heading'">
            {{ caseStudy.pageTitle }}
          </h1>
        </div>
        <div
          v-if="caseStudy.headerIntroText"
          ref="introText"
          class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--7"
          :data-id="sectionSlugs.introduction"
        >
          <RichText
            :data="caseStudy.headerIntroText"
          />
        </div>
      </div>
    </DsSection>

    <!-- Metrics -->
    <DsHighlightBoxMetrics
      v-if="computedMetrics && computedMetrics.length"
      :data-id="sectionSlugs.impact"
      :items="computedMetrics"
    />

    <!-- Video -->
    <DsSection
      v-if="caseStudy.videoSection && caseStudy.videoSection.video"
      :data-id="sectionSlugs.video"
      :theme="caseStudy.videoSection.theme"
      class="ds-lg-margin-top-96"
    >
      <div class="ds-grid">
        <h2 class="ds-grid__col ds-grid__col--12 ds-heading-small ds-margin-bottom-24">
          {{ caseStudy.videoSection.title }}
        </h2>
        <span class="ds-grid__col ds-grid__col--12 ds-text ">
          {{ caseStudy.videoSection.description }}
        </span>
        <DsVideoPlayer
          :aria-label-play="labels.play"
          :screen="screen"
          v-bind="caseStudy.videoSection.video"
          class="ds-grid__col ds-grid__col--12 ds-margin-top-24"
        />
      </div>
    </DsSection>

    <!-- Horizontal On Page Navigation -->
    <div
      v-if="showOnPageNavigation"
      class="kh-onpage-nav sticky-top ds-width-full"
    >
      <DsOnPageNavigation
        disposition="horizontal"
        :items="onPageNavItems"
        :screen="screen"
        :title="labels.onThisPage"
        :show-numbers="false"
        :update-route="true"
        :initial-active-item="$route.hash"
        portal-target="header"
        use-grid
      />
    </div>

    <!-- Chapters -->
    <KnowledgeHubArticleChapters
      v-if="caseStudy.chapters && caseStudy.chapters.length"
      :sidebar-tags="sidebarTags"
      :intro="caseStudy.chaptersIntroText"
      :chapters="caseStudy.chapters"
      :summaries="computedSummaries"
      :labels="labels"
      :page-seo-data="pageSeoData"
      :summary-margin-top="summaryMarginTop"
      @summary-changed="handleSummaryChanged"
    />

    <!-- CTA -->
    <DsCallToAction
      v-if="caseStudy.callToAction"
      :title="caseStudy.callToAction.title"
      :text="caseStudy.callToAction.text"
      :primary-link="caseStudy.callToAction.primaryLink"
      :secondary-link="caseStudy.callToAction.secondaryLink"
      :with-space-top="false"
    />

    <!-- Related Content -->
    <KnowledgeHubRelatedContent
      :related-content="caseStudy.relatedContent"
      :see-all-label="labels.seeAllKnowledgeHubArticles"
      :parent-page-url="parentPageUrl"
    />
  </div>
</template>

<script setup>
import { kebabCase } from 'lodash-es'
import {
  DsSection,
  DsOnPageNavigation,
  DsVideoPlayer,
  DsCallToAction,
  DsHighlightBoxMetrics
} from 'awds'

const { isScreenLarge, isScreenMedium, isScreenSmall, screen } = useScreen()
const route = useRoute()

const { labels } = useKnowledgeHubGlobalData()
const colorLight = '#ffffff'
const colorDark = '#00112C'
const colorDarkEyebrow = '#5C687C'
const scrollPoint = 409

const props = defineProps({
  pageSeoData: { type: Object, default: null },
  caseStudy: { type: Object, default: null },
  sidebarTags: { type: Array, default: () => [] },
  parentPageUrl: { type: String, default: '' }
})

const sectionsMap = ref({
  introduction: 'introduction',
  impact: 'impact',
  video: 'watchTheVideo',
  story: 'readTheStory'
})
const sectionIntroduction = ref('')
const introTextTopSpacing = ref(0)
const summaryMarginTop = ref('')
const eyebrowHeight = ref(0)
const titleHeight = ref(0)
const infoBlockHeight = ref(0)
const infoTopPosition = ref(0)
const isNavigationVisible = ref(false)

const media = ref()
const infoTitle = ref()
const infoEyebrow = ref()
const infoText = ref()
const introText = ref()

const sectionSlugs = computed(() => {
  return Object.fromEntries(Object.entries(sectionsMap.value).map(([slug, _]) => [slug, slug]))
})

const computedSummaries = computed(() => {
  return props.caseStudy?.summaries.map((summary) => {
    return {
      description: summary.description,
      title: summary.title
    }
  })
})

const computedMetrics = computed(() => {
  return props.caseStudy.metrics?.items.map((item) => {
    return {
      metric: item.originalMetric,
      description: item.description
    }
  })
})

const chapters = computed(() => {
  return props.caseStudy?.chapters?.filter(Boolean)
})

const onPageNavItems = computed(() => {
  return chapters.value?.filter(chapter => chapter?.navigationTitle || chapter?.title)?.map(chapter => ({
    label: chapter.navigationTitle || chapter.title,
    slug: kebabCase(chapter.navigationTitle || chapter.title)
  }))
})

const showOnPageNavigation = computed(() => {
  return isNavigationVisible.value && chapters.value?.length >= 3 && onPageNavItems.value?.length
})

const onNewCaseStudyMount = async () => {
  await nextTick()
  eyebrowHeight.value = infoEyebrow.value.offsetHeight
  titleHeight.value = infoTitle.value.offsetHeight
  infoBlockHeight.value = infoText.value.offsetHeight
  applyTopSpacing()
  const screenLargeOrMedium = isScreenLarge.value || isScreenMedium.value
  if (screenLargeOrMedium) {
    window.addEventListener('scroll', changePageInfoPositionAndColor)
    setInitialPageInfoTopPosition()
  } else {
    infoTitle.value.style.background = colorDark
    infoEyebrow.value.style.background = colorDarkEyebrow
  }
}

onMounted(() => {
  onNewCaseStudyMount()
})

watch(() => route.params.slug, onNewCaseStudyMount)

watch(isScreenSmall, (value) => {
  if (!value) {
    window.addEventListener('scroll', changePageInfoPositionAndColor)
  } else {
    window.removeEventListener('scroll', changePageInfoPositionAndColor)
    infoTitle.value.style.background = colorDark
    infoEyebrow.value.style.background = colorDarkEyebrow
    infoText.style.top = 0
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', changePageInfoPositionAndColor)
})

const setInitialPageInfoTopPosition = () => {
  const _infoText = infoText.value
  _infoText.style.top = `-${scrollPoint}px`
  infoTitle.value.style.background = colorLight
  infoEyebrow.value.style.background = colorLight
}

const applyTopSpacing = () => {
  introTextTopSpacing.value = infoBlockHeight.value + 48
  introText.value.style.paddingTop = `${introTextTopSpacing.value}px`
}

const changePageInfoPositionAndColor = () => {
  const _infoText = infoText.value
  let titleOnMedia = 0
  let eyebrowOnMedia = 0
  const infoBlockOnMediaPosition = -144
  if (window.scrollY <= scrollPoint) {
    infoTopPosition.value = window.scrollY - scrollPoint
    _infoText.style.top = `${infoTopPosition.value}px`
    if (infoTopPosition.value < (infoBlockOnMediaPosition - infoBlockHeight.value)) {
      infoTitle.value.style.background = colorLight
      infoEyebrow.value.style.background = colorLight
    } else if (infoTopPosition.value >= (infoBlockOnMediaPosition - infoBlockHeight.value) && infoTopPosition.value <= (infoBlockOnMediaPosition - infoBlockHeight.value + titleHeight.value)) {
      titleOnMedia = -infoTopPosition.value + infoBlockOnMediaPosition - (infoBlockHeight.value - titleHeight.value)
      infoTitle.value.style.background = `linear-gradient(to bottom, ${colorLight} ${titleOnMedia}px, ${colorDark} ${titleOnMedia + 1}px )`
    } else if (infoTopPosition.value > infoBlockOnMediaPosition - (infoBlockHeight.value - titleHeight.value)) {
      infoTitle.value.style.background = colorDark
      if (infoTopPosition.value >= infoBlockOnMediaPosition - eyebrowHeight.value && infoTopPosition.value <= infoBlockOnMediaPosition) {
        eyebrowOnMedia = -infoTopPosition.value + infoBlockOnMediaPosition
        infoEyebrow.value.style.background = `linear-gradient(to bottom, ${colorLight} ${eyebrowOnMedia}px, ${colorDarkEyebrow} ${eyebrowOnMedia + 1}px )`
      }
      if (infoTopPosition.value > infoBlockOnMediaPosition) {
        infoEyebrow.value.style.background = colorDarkEyebrow
      }
    }
  }
}

const handleSummaryChanged = (summaryEnabled) => {
  // Set navigation visible when summary is not enabled
  isNavigationVisible.value = !summaryEnabled
}

</script>
<style lang="scss" scoped>
@import 'awds/foundation-vars.scss';
@import 'awds/foundation-mixins.scss';

.header-media-container {
  aspect-ratio: 16/9;
  width: 100%;
  max-height: 80dvh;

  .header-media {
    object-fit: cover;
    height: 100%;
    width: 100%;
    object-position: center;
  }
}

.sticky-nav {
  z-index: 2;
}

.info-text {
  position: absolute;
  top: 0;
}

.info-eyebrow, .infoTitle {
  background-clip: text !important;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
}

.case-study {
  &:deep(.kh-chapter .ds-rich-text > h4) {
    @include heading-xsmall;
    margin-top: $spacing-48;
  }
}
</style>
